<template>
  <ReviewStep
    data-testid="suggestion-review"
    class="suggestion-review"
    page-title=""
    :title="`Revise a sugestão de ${title}`"
    @back="goBack"
  >
    <main class="suggestion-review__content">
      <SuggestionDetails
        :suggestion-details="suggestionDetails"
        :is-investment="isInvestment"
      />
    </main>
    <RiskValidation
      v-if="showRiskValidation && investmentProfile"
      data-testid="risk-validation"
      :suggestion-details="suggestionDetails"
      :show-risk-validation="showRiskValidation"
      :products-ids="productsIds"
      :investment-profile="investmentProfile"
      submit-button-text="Continuar"
      suggest-investment
      @on-risk-validation-success="onRiskValidationFinish"
      @on-risk-validation-abort="onRiskValidationAbort"
    />
    <PortfolioTermsModal
      v-if="portfolioId"
      :portfolio-id="portfolioId"
      :portfolio-terms="portfolioTerms"
      :is-open="showPortfolioTerms"
      @close="showPortfolioTerms = false"
      @accepted="emitAccepted"
    />
    <RejectModal
      :is-open="isRejectModalOpen"
      @rejected="emitRejected"
      @close="handleRejectModal"
    />
    <template #button-container>
      <ButtonContainer>
        <template #button-primary>
          <ButtonPrimary
            v-if="!isImpersonateFlag"
            :loading="isLoading"
            label="Aceitar"
            size="large"
            @click="acceptSuggestion"
          />
        </template>
        <template #button-secondary>
          <ButtonSecondary
            label="Recusar"
            size="large"
            @click="rejectSuggestion"
          />
        </template>
      </ButtonContainer>
    </template>
  </ReviewStep>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import {
  ReviewStep,
  ButtonContainer,
  ButtonPrimary,
  ButtonSecondary
} from '@warrenbrasil/nebraska-web';
import { PortfolioTermsModal } from '@/modules/common/components/PortfolioTermsModal';
import { RiskValidation } from '@/modules/common/components/RiskValidation';
import { RejectModal } from './components/RejectModal';
import { SuggestionDetails } from '../SuggestionDetails';
import { EInvestmentSuggestionsSteps } from '@/modules/wealth/views/InvestmentSuggestions/helpers';
import { IGetSuggestionDetailsResponse } from '@/modules/wealth/services/investment-suggestions';
import { IInvestmentProfile } from '@/types/models';
import { namespace } from 'vuex-class';
import { getCustomerInvestmentProfile } from '@/modules/common/services/http/account';
import { isImpersonate } from '@/modules/common/services/partner';
import { IPortfolioTerms } from '@/modules/wealth/services/portfolio-terms';
import * as trackEvents from '@/modules/wealth/views/InvestmentSuggestions/track-events';

const CustomerModule = namespace('CustomerModule');

@Component({
  components: {
    ReviewStep,
    SuggestionDetails,
    ButtonContainer,
    ButtonPrimary,
    ButtonSecondary,
    RiskValidation,
    RejectModal,
    PortfolioTermsModal
  }
})
export default class SuggestionReview extends Vue {
  @CustomerModule.Getter('getCustomerId')
  readonly customerId!: string;

  @Prop({ type: Object, required: true })
  private suggestionDetails!: IGetSuggestionDetailsResponse;

  @Prop({ type: Object })
  private portfolioTerms?: IPortfolioTerms;

  @Emit('change-step')
  emitChangeStep(_step: EInvestmentSuggestionsSteps) {}

  @Emit('accepted')
  emitAccepted() {
    return {
      transactionId: this.suggestionDetails.transaction.transactionId,
      status: 'accepted',
      customerId: this.customerId,
      updatedBy: this.customerId,
      portfolioId: this.suggestionDetails.transaction.portfolioApiId
    };
  }

  @Emit('rejected')
  emitRejected() {
    return {
      transactionId: this.suggestionDetails.transaction.transactionId,
      status: 'rejected',
      customerId: this.customerId,
      updatedBy: this.customerId
    };
  }

  isLoading = false;
  isRejectModalOpen = false;
  showRiskValidation = false;
  showPortfolioTerms = false;
  investmentProfile: IInvestmentProfile = {};

  get title() {
    return this.isInvestment ? 'investimento' : 'resgate';
  }

  get isInvestment() {
    return this.suggestionDetails.transaction?.direction === 'Investment';
  }

  get productsIds() {
    return this.suggestionDetails.transaction?.products?.map(
      item => item.productApiId
    );
  }

  get portfolioId() {
    return this.suggestionDetails.transaction?.portfolioApiId;
  }

  get isImpersonateFlag() {
    return isImpersonate();
  }

  async created() {
    await this.getCustomerProfile();
  }

  async getCustomerProfile() {
    const { investmentProfile } = await getCustomerInvestmentProfile(
      this.customerId
    );
    this.investmentProfile = investmentProfile;
  }

  acceptSuggestion() {
    this.isLoading = true;
    const isAdvancedInvestment =
      this.suggestionDetails.transaction.direction === 'Investment' &&
      (!this.suggestionDetails?.portfolio ||
        this.suggestionDetails?.portfolio?.managementType === 'Manual');
    if (isAdvancedInvestment) {
      if (
        this.suggestionDetails?.agreements?.requestTerms ||
        this.suggestionDetails.agreements.requestRisks
      ) {
        this.showRiskValidation = true;
        return;
      }
    }

    const isWarrenManagement =
      this.suggestionDetails.transaction.direction === 'Investment' &&
      this.suggestionDetails?.portfolio?.managementType === 'Warren';
    if (isWarrenManagement && this.portfolioTerms?.requestRisksTerm) {
      this.showPortfolioTerms = true;
      return;
    }

    this.isLoading = false;
    this.emitAccepted();
  }

  onRiskValidationFinish() {
    this.isLoading = false;
    this.emitAccepted();
  }

  onRiskValidationAbort() {
    this.showRiskValidation = false;
  }

  rejectSuggestion() {
    trackEvents.trackRefuseInvestmentSuggestion();
    this.handleRejectModal();
  }

  handleRejectModal() {
    this.isRejectModalOpen = !this.isRejectModalOpen;
  }

  goBack() {
    this.emitChangeStep(EInvestmentSuggestionsSteps.VIEW);
  }
}
</script>

<style lang="less" scoped>
.suggestion-review {
  /deep/ .review-step__title {
    max-width: 380px;
    margin: auto auto @size-spacing-x600;
    text-align: center;
  }

  &__content {
    max-width: 488px;
    margin: 0 auto @size-spacing-x1800;
    padding-bottom: @size-spacing-x1800;
  }
}
</style>
